import React, { useMemo } from "react";
import {
  TrendingUp,
  TrendingDown,
  Building2,
  IndianRupee,
  CheckCircle2,
  Clock,
  AlertCircle,
  MapPin,
  BarChart3,
  ArrowRightLeft,
  Wallet,
  Loader2,
} from "lucide-react";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
} from "recharts";
import { motion } from "framer-motion";
import {
  useDashboard,
  DashboardStats,
  CityWiseHotel,
} from "./hooks/useDashboard";

const MetricCard = React.memo(
  ({
    title,
    value,
    trend,
    icon: Icon,
  }: {
    title: string;
    value: string | number;
    trend: string;
    icon: any;
  }) => (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-[#111c44] p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-white/10 hover:border-slate-300 dark:hover:border-white/20 transition-colors"
    >
      <div className="flex justify-between items-start mb-4">
        <div className="p-3 rounded-xl bg-slate-50 dark:bg-[#1b254b] text-slate-600 dark:text-slate-400 border border-slate-100 dark:border-white/5">
          <Icon size={22} className="stroke-2" />
        </div>
        <div className="text-xs font-semibold px-2.5 py-1 rounded-lg bg-slate-50 dark:bg-[#1b254b] text-slate-500 dark:text-slate-400 border border-slate-100 dark:border-white/5">
          {trend}
        </div>
      </div>
      <div>
        <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">
          {title}
        </p>
        <h3 className="text-2xl font-bold text-slate-800 dark:text-slate-100">
          {value}
        </h3>
      </div>
    </motion.div>
  ),
);

const RegionalChart = React.memo(
  ({ cityWiseHotels }: { cityWiseHotels: CityWiseHotel[] }) => (
    <motion.div
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      className="lg:col-span-2 bg-white dark:bg-[#111c44] p-6 md:p-8 rounded-2xl shadow-sm border border-slate-200 dark:border-white/10"
    >
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div className="space-y-1">
          <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100">
            Regional Distribution
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">
            Hotel density across major cities
          </p>
        </div>
      </div>

      <div className="h-[340px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={cityWiseHotels}
            margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              stroke="currentColor"
              className="text-slate-200 dark:text-slate-800"
            />
            <XAxis
              dataKey="City"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fontWeight: 500, fill: "#64748b" }}
              dy={10}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fontWeight: 500, fill: "#64748b" }}
            />
            <Tooltip
              cursor={{ fill: "transparent" }}
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  return (
                    <div className="bg-white dark:bg-[#1b254b] p-4 rounded-xl shadow-lg border border-slate-200 dark:border-white/10">
                      <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1">
                        {payload[0].payload.City}
                      </p>
                      <p className="text-sm font-bold text-slate-800 dark:text-slate-100">
                        {payload[0].value} Hotels
                      </p>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Bar dataKey="HotelCounts" radius={[4, 4, 0, 0]} maxBarSize={60}>
              {cityWiseHotels.map((_, index) => (
                <Cell key={`cell-${index}`} fill="#2563eb" />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </motion.div>
  ),
);

const FinancialPulse = React.memo(({ stats }: { stats: DashboardStats }) => {
  const financialMetrics = useMemo(
    () => [
      {
        title: "MTD Balance",
        value: `₹${stats.MTDBalance.toLocaleString()}`,
        sub: "Net liquidity available",
        icon: Wallet,
        color: "text-blue-600 bg-blue-50 border-blue-100",
      },
      {
        title: "Today Income",
        value: `₹${stats.TodayIncome.toLocaleString()}`,
        sub: "Real-time daily flow",
        icon: TrendingUp,
        color: "text-emerald-600 bg-emerald-50 border-emerald-100",
      },
      {
        title: "Today Expense",
        value: `₹${stats.TodayExpense.toLocaleString()}`,
        sub: "Operational costs",
        icon: TrendingDown,
        color: "text-rose-600 bg-rose-50 border-rose-100",
      },
      {
        title: "MTD Expense",
        value: `₹${stats.MTDExpense.toLocaleString()}`,
        sub: "Monthly overheads",
        icon: Clock,
        color: "text-amber-600 bg-amber-50 border-amber-100",
      },
    ],
    [stats],
  );

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className="bg-white dark:bg-[#111c44] p-6 md:p-8 rounded-2xl shadow-sm border border-slate-200 dark:border-white/10 flex flex-col"
    >
      <div className="flex flex-col mb-8">
        <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100 mb-1">
          Financial Pulse
        </h3>
        <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">
          Key financial metrics overview
        </p>
      </div>

      <div className="space-y-6 flex-1">
        {financialMetrics.map((stat, i) => (
          <div
            key={i}
            className="flex gap-4 items-center p-3 rounded-xl hover:bg-slate-50 dark:hover:bg-[#1b254b]/50 transition-colors border border-transparent hover:border-slate-100 dark:hover:border-white/5"
          >
            <div
              className={`p-3 h-12 w-12 flex items-center justify-center rounded-xl border ${stat.color} shrink-0`}
            >
              <stat.icon size={20} className="stroke-2" />
            </div>
            <div className="space-y-0.5 min-w-0 flex-1">
              <p className="text-sm font-semibold text-slate-500 dark:text-slate-400 truncate">
                {stat.title}
              </p>
              <p className="text-lg font-bold text-slate-800 dark:text-slate-100 truncate">
                {stat.value}
              </p>
            </div>
          </div>
        ))}
      </div>

      <button className="w-full mt-6 py-3 rounded-xl bg-slate-50 dark:bg-[#1b254b] hover:bg-slate-100 dark:hover:bg-[#1b254b]/80 border border-slate-200 dark:border-white/10 text-slate-600 dark:text-slate-300 font-semibold text-sm transition-all focus:ring-2 focus:ring-slate-200 dark:focus:ring-white/10 outline-none">
        View Detailed Report
      </button>
    </motion.div>
  );
});

const RegionalMatrix = React.memo(
  ({
    cityWiseHotels,
    totalHotels,
  }: {
    cityWiseHotels: CityWiseHotel[];
    totalHotels: number;
  }) => (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="bg-white dark:bg-[#111c44] rounded-2xl shadow-sm border border-slate-200 dark:border-white/10 overflow-hidden"
    >
      <div className="px-6 py-5 border-b border-slate-100 dark:border-white/10 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="space-y-1">
          <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100">
            Regional Matrix
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">
            Strategic asset distribution by regional zones
          </p>
        </div>
      </div>

      {/* Desktop view */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="bg-slate-50 dark:bg-[#1b254b]/50 border-b border-slate-100 dark:border-white/5">
              <th className="px-6 py-4 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                Terminal Zone
              </th>
              <th className="px-6 py-4 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                Asset Count
              </th>
              <th className="px-6 py-4 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                Coverage Index
              </th>
              <th className="px-6 py-4 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider text-right">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-white/5">
            {cityWiseHotels.map((city, i) => (
              <tr
                key={i}
                className="hover:bg-slate-50/50 dark:hover:bg-slate-800 transition-colors"
              >
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-[#1b254b] border border-slate-200 dark:border-white/5 flex items-center justify-center text-slate-500 dark:text-slate-400">
                      <MapPin size={16} />
                    </div>
                    <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                      {city.City}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm font-medium text-slate-600 dark:text-slate-400">
                    {city.HotelCounts} Assets
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="w-full max-w-[120px] h-2 bg-slate-100 dark:bg-[#1b254b] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-500 rounded-full"
                      style={{
                        width: `${(city.HotelCounts / totalHotels) * 100}%`,
                      }}
                    />
                  </div>
                </td>
                <td className="px-6 py-4 text-right">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 text-xs font-medium border border-emerald-100 dark:border-white/10">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    Active
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Grid view */}
      <div className="lg:hidden p-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
        {cityWiseHotels.map((city, i) => (
          <div
            key={i}
            className="p-4 bg-white dark:bg-[#111c44] border border-slate-200 dark:border-white/10 rounded-xl space-y-4"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-[#1b254b] border border-slate-200 dark:border-white/5 flex items-center justify-center text-slate-500 dark:text-slate-400">
                  <MapPin size={16} />
                </div>
                <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                  {city.City}
                </span>
              </div>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 text-xs font-medium border border-emerald-100 dark:border-emerald-800">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                Active
              </span>
            </div>

            <div className="space-y-1.5">
              <div className="flex justify-between text-xs font-medium text-slate-500 dark:text-slate-400">
                <span>Coverage</span>
                <span className="text-slate-700 dark:text-slate-200 font-semibold">
                  {city.HotelCounts} Hotels
                </span>
              </div>
              <div className="w-full h-1.5 bg-slate-100 dark:bg-[#1b254b] rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-500 rounded-full"
                  style={{
                    width: `${(city.HotelCounts / totalHotels) * 100}%`,
                  }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  ),
);

const OneDeskDashboard = () => {
  const { data, loading, refresh } = useDashboard();

  const coreMetrics = useMemo(() => {
    if (!data) return [];
    return [
      {
        title: "MTD Revenue",
        value: `₹${data.MTDRevenue.toLocaleString()}`,
        trend: "Global Income",
        icon: IndianRupee,
      },
      {
        title: "Active Hotels",
        value: data.ActiveHotels,
        trend: `${data.PendingHotels} Pending`,
        icon: Building2,
      },
      {
        title: "MTD Bookings",
        value: data.MTDBookings,
        trend: `YTD: ${data.YTDBookings}`,
        icon: BarChart3,
      },
      {
        title: "MTD ADR",
        value: `₹${data.MTD_ADR.toLocaleString()}`,
        trend: "Avg Daily Rate",
        icon: CheckCircle2,
      },
    ];
  }, [data]);

  if (loading) {
    return (
      <div className="h-[80vh] w-full flex flex-col items-center justify-center gap-4">
        <div className="relative">
          <Loader2 size={48} className="text-blue-600 animate-spin" />
          <div className="absolute inset-0 blur-xl bg-blue-400/20 animate-pulse"></div>
        </div>
        <p className="text-slate-500 dark:text-slate-400 font-bold animate-pulse uppercase tracking-[0.2em] text-xs">
          Synchronizing Super Panel...
        </p>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-8 animate-fadeIn pb-12">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 bg-white dark:bg-[#111c44] p-8 rounded-2xl border border-slate-200 dark:border-white/10 shadow-sm">
        <div className="space-y-2">
          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 text-xs font-bold uppercase tracking-wider">
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-blue-600"></span>
            </span>
            Live Analytics
          </div>
          <h1 className="text-3xl font-bold text-slate-800 dark:text-slate-100 tracking-tight">
            Command Center
          </h1>
          <p className="text-slate-500 dark:text-slate-400 font-medium max-w-xl">
            Enterprise-grade intelligence for RoooMix. Overseeing{" "}
            <span className="text-slate-700 dark:text-slate-200 font-semibold">
              {data.TotalHotels}
            </span>{" "}
            hotels across{" "}
            <span className="text-slate-700 dark:text-slate-200 font-semibold">
              {data.CityWiseHotels?.length}
            </span>{" "}
            regions.
          </p>
        </div>

        <button
          onClick={refresh}
          className="h-11 px-6 bg-white dark:bg-[#1b254b] border border-slate-200 dark:border-white/10 hover:bg-slate-50 dark:hover:bg-[#1b254b]/80 text-slate-700 dark:text-slate-200 rounded-xl text-sm font-semibold transition-all flex items-center justify-center gap-2 active:scale-95 shadow-sm"
        >
          <ArrowRightLeft size={16} />
          Refresh Data
        </button>
      </div>

      {/* Core Metrics Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {coreMetrics.map((stat, i) => (
          <MetricCard key={i} {...stat} />
        ))}
      </div>

      {/* Financial Intelligence & Regional Distribution */}
      <div className="grid lg:grid-cols-3 gap-6">
        <RegionalChart cityWiseHotels={data.CityWiseHotels} />
        <FinancialPulse stats={data} />
      </div>

      {/* Expired Asset Monitoring */}
      {data.ExpireHotels > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-50 border border-red-100 rounded-[32px] p-6 flex items-center gap-6"
        >
          <div className="h-12 w-12 rounded-2xl bg-red-500 text-white flex items-center justify-center shadow-lg shadow-red-500/20">
            <AlertCircle size={24} />
          </div>
          <div>
            <h4 className="text-red-900 font-black tracking-tight">
              Protocol Alert: Expired Assets Detected
            </h4>
            <p className="text-red-600 text-sm font-medium">
              {data.ExpireHotels} hotel licenses have reached terminal
              expiration. Immediate synchronization required.
            </p>
          </div>
          <button className="ml-auto px-6 py-2.5 bg-red-600 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-red-700 transition-all shadow-md active:scale-95">
            Initiate Recovery
          </button>
        </motion.div>
      )}

      {/* Regional Data Matrix Section */}
      <RegionalMatrix
        cityWiseHotels={data.CityWiseHotels}
        totalHotels={data.TotalHotels}
      />
    </div>
  );
};

export default OneDeskDashboard;
